import { FC } from 'react';
import { DraggableDocumentItemProps } from './DraggableDocumentItem.types';
import { useDraggableItem } from '@/_hooks/useDraggableItem';
import { Flex } from '@/_components/Flex';
import { Text } from '@/_components/Text';
import { Pill } from '@/_components/Pill';
import { Button } from '@/_components/Button';
import { ButtonGroup } from '@/_components/ButtonGroup';
import { Icon } from '@/_components/Icon';
import styles from './DraggableDocumentItem.module.css';

export const DraggableDocumentItem: FC<DraggableDocumentItemProps> = ({
  document,
  documentName,
  documentDescription,
  infoPillText,
  isDisabled,
  isRemoving,
  isProcessing,
  isDocumentProcessing,
  isSplitLoading,
  isFromSplitDocument,
  onPreview,
  onRemove,
  splitDocument,
  user,
  mode,
  preferences,
}) => {
  const { attributes, listeners, setNodeRef, style, isDragging } =
    useDraggableItem(document, isDisabled);

  if (isFromSplitDocument && mode === 'client') {
    return null;
  }

  return (
    <div ref={setNodeRef} style={style}>
      {preferences?.append_description_to_filename_enabled ? (
        <Flex
          className={styles.fileWrapper}
          isYCentered={true}
          isBetween={true}
          gap={4}
        >
          <Flex isYCentered={true} gap={4}>
            {!isDisabled && (
              <div
                {...attributes}
                {...listeners}
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
              >
                <Icon name="grab" size={20} />
              </div>
            )}
            <Text type="small" isEllipsis={true} takeAvailableSpace>
              {documentName}
            </Text>
            {documentDescription && (
              <Pill
                label={documentDescription}
                intent="info"
                size="small"
                isEllipsis={true}
                className={styles.descriptionPill}
              />
            )}
            {infoPillText && (
              <Pill
                label={infoPillText}
                intent="info"
                isLoading={isProcessing}
              />
            )}
          </Flex>
          <ButtonGroup isSmallGroup={true}>
            {user?.is_advisor &&
              preferences?.bulk_upload_processor &&
              document &&
              document.toLowerCase().endsWith('.pdf') && (
                <Button
                  intent="primary"
                  value="Split"
                  size="tiny"
                  withTooltip
                  tooltip={
                    isDocumentProcessing
                      ? 'Document is still processing'
                      : 'Split the document using the Bulk Upload Processor'
                  }
                  onClick={() => splitDocument(document)}
                  isLoading={isSplitLoading}
                  isDisabled={isDocumentProcessing}
                />
              )}
            <Button
              onClick={onPreview}
              value="Preview"
              iconLeft="eye"
              isIconOnly={true}
              withTooltip
              size="tiny"
              intent="ghost"
            />
            <Button
              isDisabled={isDisabled || isRemoving}
              onClick={onRemove}
              value="Remove"
              iconLeft="trash"
              isIconOnly={true}
              withTooltip
              size="tiny"
              intent="danger-ghost"
              isLoading={isRemoving}
            />
          </ButtonGroup>
        </Flex>
      ) : (
        <Flex
          isYCentered={true}
          isBetween={true}
          gap={4}
          className={styles.fileWrapper}
        >
          <Flex
            gap={4}
            isYCentered={true}
            className={styles.draggableDocumentItem__container}
          >
            {!isDisabled && (
              <div
                {...attributes}
                {...listeners}
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
              >
                <Icon name="grab" size={20} internalIconWidth="bold" />
              </div>
            )}
            <Flex
              isColumn={true}
              gap={2}
              className={styles.draggableDocumentItem__containerItem}
            >
              <Text type="small" isEllipsis={true} takeAvailableSpace>
                {documentName}
              </Text>
              {infoPillText && (
                <Pill
                  isEllipsis={true}
                  label={infoPillText}
                  intent="info"
                  isLoading={isProcessing}
                />
              )}
            </Flex>
          </Flex>
          <ButtonGroup isSmallGroup={true} isSameLine={true}>
            {user?.is_advisor &&
              preferences?.bulk_upload_processor &&
              document &&
              document.toLowerCase().endsWith('.pdf') && (
                <Button
                  intent="primary"
                  value="Split"
                  size="tiny"
                  withTooltip
                  tooltip={
                    isDocumentProcessing
                      ? 'Document is still processing'
                      : 'Split the document using the Bulk Upload Processor'
                  }
                  onClick={() => splitDocument(document)}
                  isLoading={isSplitLoading}
                  isDisabled={isDocumentProcessing}
                />
              )}
            <Button
              onClick={onPreview}
              value="Preview"
              iconLeft="eye"
              isIconOnly={true}
              withTooltip
              size="tiny"
              intent="ghost"
            />
            <Button
              isDisabled={isDisabled || isRemoving}
              onClick={onRemove}
              value="Remove"
              iconLeft="trash"
              isIconOnly={true}
              withTooltip
              size="tiny"
              intent="danger-ghost"
              isLoading={isRemoving}
            />
          </ButtonGroup>
        </Flex>
      )}
    </div>
  );
};
